import React, { useEffect, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import 'cesium/Build/Cesium/Widgets/widgets.css';

// Configure Cesium for development environment
window.CESIUM_BASE_URL = '/cesium/';
Cesium.buildModuleUrl.setBaseUrl('/cesium/');

// Set Cesium Ion access token
Cesium.Ion.defaultAccessToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJqdGkiOiI4Mzk3NDRkOC1jM2YwLTQ2ZDgtODNhMy0yNDE1N2RkNDJlZGQiLCJpZCI6MzQwODk5LCJpYXQiOjE3NTc3MjAxMjF9.TeWYZ8SQ_t-QaO8Si48fFwPqOk5IhBtvP4syuEg9ef0';

// Set the default ellipsoid to Mars instead of WGS84 Earth
Cesium.Ellipsoid.default = Cesium.Ellipsoid.MARS;

const MarsGlobeVisualizer = () => {
  const viewerRef = useRef(null);
  const viewerInstanceRef = useRef(null);
  const clickHandlerRef = useRef(null); // Store click handler reference
  const [viewerReady, setViewerReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [locationFilter, setLocationFilter] = useState('all'); // Filter for location types
  const isMountedRef = useRef(true); // Track if component is still mounted
  const isInitializingRef = useRef(false); // Track if async initialization is in progress
  const pendingOperationsRef = useRef(new Set()); // Track pending async operations
  const cleanupTimeoutRef = useRef(null); // Track cleanup timeout

  // Key Mars locations with coordinates and information
  const marsLocations = [
    {
      id: 'olympus_mons',
      name: 'Olympus Mons',
      type: 'Volcano',
      coordinates: { longitude: 226.2, latitude: 18.7 },
      description: 'The largest volcano in the solar system, standing nearly 22 km high and 600 km wide at its base.',
      facts: [
        'Nearly 3 times taller than Mount Everest',
        'Last erupted about 25 million years ago',
        'Has a massive caldera 80 km wide',
        'Surrounded by an aureole of chaotic terrain'
      ],
      significance: 'Largest known volcano in the solar system',
      image: 'https://apod.nasa.gov/apod/image/2304/OlympusMons_MarsExpress_6000.jpg'
    },
    {
      id: 'valles_marineris',
      name: 'Valles Marineris',
      type: 'Canyon System',
      coordinates: { longitude: 287.5, latitude: -13.9 },
      description: 'The largest canyon system in the solar system, stretching over 4,000 km long and up to 7 km deep.',
      facts: [
        '10 times longer than the Grand Canyon',
        '7 km deep in some places',
        'Spans about 20% of Mars\'s circumference',
        'Formed by tectonic activity and collapse'
      ],
      significance: 'Largest known canyon in the solar system',
      image: 'https://apod.nasa.gov/apod/image/2411/marsglobe_viking_1552.jpg'
    },
    {
      id: 'gale_crater',
      name: 'Gale Crater',
      type: 'Impact Crater',
      coordinates: { longitude: 137.4, latitude: -5.4 },
      description: 'Home to NASA\'s Curiosity rover. A 154 km wide crater with a 5 km high central mountain.',
      facts: [
        '154 km in diameter',
        'Central mountain is 5.5 km high',
        'Contains evidence of ancient lake beds',
        'Curiosity has been exploring since 2012'
      ],
      significance: 'Curiosity rover landing site, ancient lake evidence',
      image: 'https://assets.science.nasa.gov/dynamicimage/assets/science/psd/mars/resources/detail_files/4/4428_pia16052-figure_3color-full2.jpg?w=1449&h=897&fit=clip&crop=faces%2Cfocalpoint'
    },
    {
      id: 'jezero_crater',
      name: 'Jezero Crater',
      type: 'Impact Crater',
      coordinates: { longitude: 77.5, latitude: 18.4 },
      description: 'Landing site of NASA\'s Perseverance rover. Contains an ancient river delta indicating past water activity.',
      facts: [
        '45 km in diameter',
        'Contains a well-preserved river delta',
        'Evidence of ancient lake environment',
        'Perseverance landed here in 2021'
      ],
      significance: 'Perseverance rover landing site, astrobiology research',
      image: 'https://www.nasa.gov/wp-content/uploads/2023/03/25538_pia24310_-_farley_traverse_-_unannotated-web_1.jpg'
    },
    {
      id: 'hellas_planitia',
      name: 'Hellas Planitia',
      type: 'Impact Basin',
      coordinates: { longitude: 70.0, latitude: -42.4 },
      description: 'The largest impact crater on Mars, spanning 2,300 km across and 9 km deep.',
      facts: [
        '2,300 km in diameter',
        '9 km deep',
        'Contains the lowest point on Mars',
        'Formed by a massive asteroid impact'
      ],
      significance: 'Largest confirmed impact crater in the solar system',
      image: 'https://www.nasa.gov/wp-content/uploads/2023/03/451054main_mro20100505-full_full.jpg'
    },
    {
      id: 'tharsis_montes',
      name: 'Tharsis Montes',
      type: 'Volcanic Region',
      coordinates: { longitude: 247.0, latitude: 0.9 },
      description: 'A massive volcanic plateau on Mars, home to several large volcanoes including Arsia Mons, Pavonis Mons, and Ascraeus Mons.',
      facts: [
        'Contains three major volcanoes',
        'Arsia Mons is 20 km high',
        'Volcanic activity lasted billions of years',
        'Associated with Valles Marineris formation'
      ],
      significance: 'Major volcanic province, source of Tharsis bulge'
    },
    {
      id: 'north_polar_ice_cap',
      name: 'North Polar Ice Cap',
      type: 'Polar Ice Cap',
      coordinates: { longitude: 0.0, latitude: 88.0 },
      description: 'Mars\' northern polar ice cap, composed mainly of water ice with some carbon dioxide ice.',
      facts: [
        'Up to 3 km thick in places',
        'Contains mostly water ice',
        'Seasonal carbon dioxide frost',
        'Evidence of past climate changes'
      ],
      significance: 'Major water reservoir, climate indicator'
    },
    {
      id: 'south_polar_ice_cap',
      name: 'South Polar Ice Cap',
      type: 'Polar Ice Cap',
      coordinates: { longitude: 0.0, latitude: -88.0 },
      description: 'Mars\' southern polar ice cap, more permanent than the northern cap with a thicker carbon dioxide layer.',
      facts: [
        'Thicker carbon dioxide layer',
        'Contains water ice underneath',
        'Shows spiral troughs from katabatic winds',
        'More stable than northern ice cap'
      ],
      significance: 'Thickest polar ice, seasonal carbon dioxide cycle'
    },
    {
      id: 'arsia_mons',
      name: 'Arsia Mons',
      type: 'Shield Volcano',
      coordinates: { longitude: 239.9, latitude: -8.4 },
      description: 'The southernmost and largest of the Tharsis Montes volcanoes, featuring extensive lava flows and a massive caldera.',
      facts: [
        'About 435 km in diameter at base',
        '9 km high from base to peak',
        'Contains the deepest point in Tharsis region',
        'Shows evidence of recent glacial activity'
      ],
      significance: 'Largest of Tharsis Montes volcanoes, potential recent activity'
    },

    {
      id: 'ascraeus_mons',
      name: 'Ascraeus Mons',
      type: 'Shield Volcano',
      coordinates: { longitude: 255.9, latitude: 11.9 },
      description: 'The northernmost and tallest of the Tharsis Montes volcanoes, with extensive lava flows covering thousands of square kilometers.',
      facts: [
        '18 km high from base to peak',
        'Has the largest volcanic caldera on Mars',
        'Extensive lava flows visible from space',
        'Part of the Tharsis volcanic province'
      ],
      significance: 'Tallest of Tharsis Montes, largest caldera on Mars'
    },
    {
      id: 'elysium_mons',
      name: 'Elysium Mons',
      type: 'Shield Volcano',
      coordinates: { longitude: 147.2, latitude: 25.0 },
      description: 'A large shield volcano in the Elysium volcanic province, featuring a prominent caldera and extensive lava fields.',
      facts: [
        '12.6 km high',
        '14 km wide caldera',
        'Surrounded by lava flows and channels',
        'Part of Elysium volcanic rise'
      ],
      significance: 'Major volcano in Elysium province, extensive lava flows'
    },
    {
      id: 'alba_mons',
      name: 'Alba Mons',
      type: 'Volcanic Construct',
      coordinates: { longitude: 250.4, latitude: 40.5 },
      description: 'One of the largest volcanoes on Mars by volume, though not the tallest, with a very broad, low-profile shape.',
      facts: [
        'Largest volcanic construct by volume',
        '6 km high but 1,600 km wide',
        'Very low slope angle (about 0.5°)',
        'Summit covered with lava flows'
      ],
      significance: 'Largest volcano by volume, unique broad profile'
    },
    {
      id: 'syrtis_major',
      name: 'Syrtis Major',
      type: 'Volcanic Plateau',
      coordinates: { longitude: 69.5, latitude: 8.4 },
      description: 'A massive dark volcanic plateau that appears as a prominent dark spot on Mars, formed by extensive lava flows.',
      facts: [
        '1,500 km long, 1,000 km wide',
        'Made of solidified lava flows',
        'Appears dark due to iron-rich basalt',
        'Contains numerous smaller volcanoes'
      ],
      significance: 'Largest dark feature on Mars, extensive volcanic province'
    },
    {
      id: 'acidalia_planitia',
      name: 'Acidalia Planitia',
      type: 'Lowland Plain',
      coordinates: { longitude: 339.0, latitude: 50.0 },
      description: 'A vast lowland plain in Mars\' northern hemisphere, characterized by smooth terrain and potential ancient water features.',
      facts: [
        'Over 3,000 km across',
        'Contains smooth, flat terrain',
        'May have hosted ancient lakes',
        'Site of extensive wind erosion'
      ],
      significance: 'Major northern lowland, potential ancient water activity'
    },
    {
      id: 'utopia_planitia',
      name: 'Utopia Planitia',
      type: 'Impact Basin',
      coordinates: { longitude: 117.0, latitude: 46.7 },
      description: 'Site of the Viking 2 lander, this vast plain contains evidence of ancient water activity and possible ice deposits.',
      facts: [
        '3,300 km in diameter',
        'Viking 2 landed here in 1976',
        'Contains polygonal patterned ground',
        'Evidence of past water and ice activity'
      ],
      significance: 'Viking 2 landing site, evidence of ancient water'
    },
    {
      id: 'chryse_planitia',
      name: 'Chryse Planitia',
      type: 'Lowland Plain',
      coordinates: { longitude: 320.0, latitude: 28.0 },
      description: 'Site of the Viking 1 lander and home to the largest outflow channels on Mars, indicating massive ancient floods.',
      facts: [
        'Viking 1 landed here in 1976',
        'Contains outflow channels from Valles Marineris',
        'Evidence of catastrophic flooding',
        'Site of proposed ancient ocean shorelines'
      ],
      significance: 'Viking 1 landing site, outflow channel terminus'
    },
    {
      id: 'isidis_planitia',
      name: 'Isidis Planitia',
      type: 'Impact Basin',
      coordinates: { longitude: 87.0, latitude: 12.9 },
      description: 'A large impact basin that may have once contained a lake, with well-preserved ejecta patterns and potential water history.',
      facts: [
        '1,500 km in diameter',
        'Contains well-preserved ejecta blanket',
        'May have hosted an ancient lake',
        'Shows evidence of sedimentary deposits'
      ],
      significance: 'Well-preserved impact basin, potential ancient lake'
    },
    {
      id: 'arabia_terra',
      name: 'Arabia Terra',
      type: 'Highland Region',
      coordinates: { longitude: 0.0, latitude: 20.0 },
      description: 'A large, heavily cratered highland region that shows evidence of ancient water erosion and sedimentary processes.',
      facts: [
        'Over 4,500 km across',
        'Contains valley networks from water erosion',
        'Shows layered sedimentary deposits',
        'Evidence of ancient groundwater activity'
      ],
      significance: 'Major highland with extensive water erosion evidence'
    },
    {
      id: 'candor_chasma',
      name: 'Candor Chasma',
      type: 'Canyon',
      coordinates: { longitude: 283.0, latitude: -6.0 },
      description: 'A spectacular canyon within Valles Marineris, featuring layered deposits and evidence of ancient water and wind activity.',
      facts: [
        '810 km long, up to 7 km deep',
        'Contains spectacular layered deposits',
        'Shows evidence of water and wind erosion',
        'Part of the larger Valles Marineris system'
      ],
      significance: 'Spectacular layered canyon, sedimentary history'
    },
    {
      id: 'medusae_fossae',
      name: 'Medusae Fossae',
      type: 'Wind-Sculpted Deposits',
      coordinates: { longitude: 164.0, latitude: -2.0 },
      description: 'One of the largest wind-sculpted formations on Mars, consisting of massive deposits of volcanic ash and dust.',
      facts: [
        'Over 1,000 km long',
        'Up to 3 km thick in places',
        'Sculpted by wind into yardangs',
        'May be source of Martian dust storms'
      ],
      significance: 'Largest wind-sculpted formation, dust storm source'
    },
    {
      id: 'polar_dunes',
      name: 'North Polar Sand Dunes',
      type: 'Sand Dunes',
      coordinates: { longitude: 270.0, latitude: 75.0 },
      description: 'Massive sand dunes surrounding the north polar ice cap, sculpted by seasonal winds and showing active dune movement.',
      facts: [
        'Cover thousands of square kilometers',
        'Show active dune movement',
        'Sculpted by seasonal winds',
        'Contain water ice mixed with sand'
      ],
      significance: 'Active dune field, seasonal wind patterns'
    },
    // Human Technology on Mars Surface
    {
      id: 'insight_lander',
      name: 'InSight Lander',
      type: 'NASA Lander',
      coordinates: { longitude: 135.6, latitude: 4.5 },
      description: 'NASA\'s InSight lander, deployed a seismometer and heat probe to study Mars\' interior structure.',
      facts: [
        'Landed in Elysium Planitia on November 26, 2018',
        'Deployed SEIS seismometer and HP³ heat probe',
        'Studied marsquakes and internal structure',
        'Completed primary mission in 2022'
      ],
      significance: 'First mission dedicated to studying Mars\' interior'
    },
    {
      id: 'phoenix_lander',
      name: 'Phoenix Lander',
      type: 'NASA Lander',
      coordinates: { longitude: 126.6, latitude: 68.2 },
      description: 'NASA\'s Phoenix lander, studied the arctic region of Mars and confirmed water ice near the surface.',
      facts: [
        'Landed in Green Valley on May 25, 2008',
        'Confirmed water ice in Martian soil',
        'Operated for 157 sols before winter hibernation',
        'Found perchlorates in the soil'
      ],
      significance: 'Confirmed water ice at high latitudes'
    },
    {
      id: 'spirit_rover',
      name: 'Spirit Rover',
      type: 'NASA Rover',
      coordinates: { longitude: 175.5, latitude: -14.6 },
      description: 'NASA\'s Spirit rover (MER-A), explored Gusev Crater and provided evidence of past water activity.',
      facts: [
        'Landed in Gusev Crater on January 4, 2004',
        'Operated for 2,269 sols (6 years, 3 months)',
        'Traveled over 7.7 km across Martian terrain',
        'Found evidence of past water activity'
      ],
      significance: 'Longest-operating rover until Opportunity surpassed it'
    },
    {
      id: 'opportunity_rover',
      name: 'Opportunity Rover',
      type: 'NASA Rover',
      coordinates: { longitude: 354.5, latitude: -1.9 },
      description: 'NASA\'s Opportunity rover (MER-B), explored Meridiani Planum and found evidence of ancient water.',
      facts: [
        'Landed in Meridiani Planum on January 25, 2004',
        'Operated for 5,352 sols (14 years, 3 months)',
        'Traveled over 45 km across Martian terrain',
        'Found hematite spherules ("blueberries")'
      ],
      significance: 'Longest-operating rover in history'
    },
    {
      id: 'pathfinder_sojourner',
      name: 'Pathfinder/Sojourner',
      type: 'NASA Lander/Rover',
      coordinates: { longitude: 326.5, latitude: 19.3 },
      description: 'NASA\'s Mars Pathfinder mission with the Sojourner rover, first successful rover on Mars.',
      facts: [
        'Landed in Ares Vallis on July 4, 1997',
        'Sojourner rover operated for 83 sols',
        'First rover to operate on another planet',
        'Provided detailed images and soil analysis'
      ],
      significance: 'First successful rover mission to Mars'
    },
    // Satellites Orbiting Mars
    {
      id: 'mars_reconnaissance_orbiter',
      name: 'Mars Reconnaissance Orbiter',
      type: 'NASA Orbiter',
      coordinates: { longitude: 0.0, latitude: 0.0 },
      description: 'NASA\'s Mars Reconnaissance Orbiter, providing high-resolution imaging and serving as a communications relay.',
      facts: [
        'Launched August 12, 2005, arrived March 10, 2006',
        'Orbits at approximately 255-320 km altitude',
        'Provides highest resolution images of Mars surface',
        'Serves as communications relay for surface missions'
      ],
      significance: 'Primary orbital reconnaissance and communications platform'
    },
    {
      id: 'mars_odyssey',
      name: 'Mars Odyssey',
      type: 'NASA Orbiter',
      coordinates: { longitude: 120.0, latitude: 0.0 },
      description: 'NASA\'s Mars Odyssey orbiter, studying Mars\' radiation environment and serving as a communications relay.',
      facts: [
        'Launched April 7, 2001, arrived October 24, 2001',
        'Orbits at approximately 400 km altitude',
        'Mapped hydrogen distribution indicating water ice',
        'Longest-operating spacecraft at Mars'
      ],
      significance: 'Longest-serving spacecraft at Mars, radiation monitoring'
    },
    {
      id: 'mars_express',
      name: 'Mars Express',
      type: 'ESA Orbiter',
      coordinates: { longitude: 240.0, latitude: 0.0 },
      description: 'ESA\'s Mars Express orbiter, studying Mars\' atmosphere, surface, and subsurface.',
      facts: [
        'Launched June 2, 2003, arrived December 25, 2003',
        'Carries high-resolution stereo camera and radar',
        'Detected methane in Martian atmosphere',
        'Europe\'s first mission to Mars'
      ],
      significance: 'Europe\'s first Mars mission, atmospheric studies'
    },
    {
      id: 'maven',
      name: 'MAVEN',
      type: 'NASA Orbiter',
      coordinates: { longitude: 60.0, latitude: 0.0 },
      description: 'NASA\'s MAVEN orbiter, studying Mars\' upper atmosphere and how the planet lost its air.',
      facts: [
        'Launched November 18, 2013, arrived September 21, 2014',
        'Orbits in highly elliptical path (150-6,200 km)',
        'Studies atmospheric escape processes',
        'Provides data on Mars\' climate history'
      ],
      significance: 'Studies atmospheric loss and climate evolution'
    },
    {
      id: 'mars_orbiter_mission',
      name: 'Mars Orbiter Mission',
      type: 'ISRO Orbiter',
      coordinates: { longitude: 180.0, latitude: 0.0 },
      description: 'India\'s Mars Orbiter Mission (Mangalyaan), studying Mars\' surface, atmosphere, and mineralogy.',
      facts: [
        'Launched November 5, 2013, arrived September 24, 2014',
        'First Asian mission to Mars',
        'Orbits at approximately 365-80,000 km altitude',
        'Studies surface composition and atmospheric processes'
      ],
      significance: 'India\'s first interplanetary mission'
    },
    {
      id: 'exomars_tgo',
      name: 'ExoMars Trace Gas Orbiter',
      type: 'ESA/Roscosmos Orbiter',
      coordinates: { longitude: 300.0, latitude: 0.0 },
      description: 'ESA/Roscosmos ExoMars Trace Gas Orbiter, searching for evidence of life-related gases in Mars\' atmosphere.',
      facts: [
        'Launched March 14, 2016, arrived October 19, 2016',
        'Highly sensitive trace gas detector',
        'Studies methane and other atmospheric gases',
        'Part of ESA-Russia ExoMars program'
      ],
      significance: 'Searches for atmospheric biosignatures'
    },
    {
      id: 'hope_probe',
      name: 'Hope Probe',
      type: 'UAE Orbiter',
      coordinates: { longitude: 30.0, latitude: 0.0 },
      description: 'UAE\'s Hope probe, studying Mars\' atmosphere and weather patterns from orbit.',
      facts: [
        'Launched July 19, 2020, arrived February 9, 2021',
        'First Arab and Muslim-majority country mission to Mars',
        'Highly elliptical orbit (20,000-43,000 km)',
        'Studies atmospheric dynamics and climate'
      ],
      significance: 'First Arab mission to Mars, atmospheric studies'
    },
    {
      id: 'tianwen_1',
      name: 'Tianwen-1',
      type: 'CNSA Orbiter',
      coordinates: { longitude: 270.0, latitude: 0.0 },
      description: 'China\'s Tianwen-1 mission, including orbiter, lander, and Zhurong rover exploring Mars.',
      facts: [
        'Launched July 23, 2020, arrived February 10, 2021',
        'Includes orbiter, lander, and Zhurong rover',
        'Zhurong rover explored Utopia Planitia',
        'China\'s first Mars mission'
      ],
      significance: 'China\'s first Mars mission, comprehensive exploration'
    }
  ];

  useEffect(() => {
    let mounted = true;

    const initializeMarsViewer = async () => {
      if (!viewerRef.current || viewerInstanceRef.current || !mounted) return;

      console.log('🏗️ Starting Mars Cesium viewer initialization...');
      const viewerStartTime = performance.now();

      try {
        console.log('🔧 Creating Cesium viewer for Mars with container:', viewerRef.current);

        // Create viewer with Mars-specific configuration
        const cesiumViewer = new Cesium.Viewer(viewerRef.current, {
          // Disable the globe to prevent interference with 3D Tiles
          globe: false,
          // 2D and Columbus View are not supported for global 3D Tiles
          sceneModePicker: false,
          // Don't use Earth imagery layers for Mars
          baseLayerPicker: false,
          // Geocoder is not supported for Mars locations
          geocoder: false,
          // Additional Mars-specific settings
          homeButton: false,
          timeline: false,
          animation: false,
          navigationHelpButton: false,
          fullscreenButton: false,
          infoBox: false,
          selectionIndicator: false,
          skyBox: false,
          skyAtmosphere: false,
          sun: true,
          moon: false
        });

        const viewerEndTime = performance.now();
        console.log(`⏱️ Mars Cesium viewer initialization completed in ${(viewerEndTime - viewerStartTime).toFixed(3)} seconds`);
        console.log('✅ Cesium viewer created:', cesiumViewer);
        console.log('✅ Viewer canvas:', cesiumViewer.scene?.canvas);

        // Store viewer instance
        viewerInstanceRef.current = cesiumViewer;

        // Verify viewer was created successfully
        if (!cesiumViewer || !cesiumViewer.scene || !cesiumViewer.scene.canvas) {
          console.error('❌ Failed to create Mars Cesium viewer');
          return;
        }

        // Load Mars 3D Tiles from Cesium Ion with enhanced detail settings
        console.log('🌌 Loading Mars 3D Tiles with high resolution...');

        try {
          const tileset = await Cesium.Cesium3DTileset.fromIonAssetId(3644333); // Mars 3D Tiles

          // Check if still mounted and viewer exists
          if (!mounted || !cesiumViewer || cesiumViewer.isDestroyed()) {
            console.log('⚠️ Component unmounted or viewer destroyed during tileset loading');
            return;
          }

          // Configure enhanced detail levels for better resolution
          tileset.maximumScreenSpaceError = 0.8; // Higher detail (lower = more detail)
          tileset.maximumMemoryUsage = 1536; // 1.5GB memory for high detail
          tileset.dynamicScreenSpaceError = true;
          tileset.dynamicScreenSpaceErrorDensity = 0.00278;
          tileset.dynamicScreenSpaceErrorFactor = 4.0;
          tileset.dynamicScreenSpaceErrorHeightFalloff = 0.25;

          // Progressive loading settings for smooth experience
          tileset.preloadWhenHidden = true;
          tileset.preloadFlightDestinations = true;
          tileset.skipLevelOfDetail = false;
          tileset.baseScreenSpaceError = 1024;
          tileset.skipScreenSpaceErrorFactor = 16;
          tileset.skipLevels = 1;
          tileset.immediatelyLoadDesiredLevelOfDetail = false;
          tileset.loadSiblings = false;

          // Add terrain style for enhanced visual quality
          tileset.style = new Cesium.Cesium3DTileStyle({
            show: true,
            color: "rgb(255, 255, 255)",
            pointSize: "2.0"
          });

          cesiumViewer.scene.primitives.add(tileset);
          console.log('✅ Mars 3D Tiles loaded successfully with enhanced detail');

          // Configure viewer for high visual quality
          cesiumViewer.scene.fog.enabled = false; // Clear view without fog
          cesiumViewer.resolutionScale = 1.2; // 20% higher resolution
          if (cesiumViewer.scene.postProcessStages.fxaa) {
            cesiumViewer.scene.postProcessStages.fxaa.enabled = true; // Anti-aliasing
          }

          // Set camera to view Mars with better initial position
          cesiumViewer.camera.setView({
            destination: new Cesium.Cartesian3(-2500000.0, 5000000.0, 4000000.0)
          });

          // Add location pins
          addLocationPins(cesiumViewer);

          // Mark viewer as ready
          if (mounted) {
            setViewerReady(true);
            setLoading(false);
            console.log('🎯 Mars Cesium viewer with enhanced 3D Tiles ready');
          }

        } catch (tilesetError) {
          console.error('❌ Failed to load Mars 3D Tiles:', tilesetError);

          // Fallback: Enable globe with Mars ellipsoid and enhanced visuals
          if (mounted && cesiumViewer && !cesiumViewer.isDestroyed()) {
            cesiumViewer.scene.globe = new Cesium.Globe(Cesium.Ellipsoid.MARS);
            cesiumViewer.scene.globe.enableLighting = true;
            cesiumViewer.scene.globe.terrainExaggeration = 1.5; // Match main config

            // Configure viewer for high visual quality (fallback)
            cesiumViewer.scene.fog.enabled = false;
            cesiumViewer.resolutionScale = 1.2;
            if (cesiumViewer.scene.postProcessStages.fxaa) {
              cesiumViewer.scene.postProcessStages.fxaa.enabled = true;
            }

            // Set camera for Mars globe
            cesiumViewer.camera.setView({
              destination: Cesium.Cartesian3.fromDegrees(0, 0, 8000000.0)
            });

            // Add location pins even in fallback mode
            addLocationPins(cesiumViewer);

            // Mark viewer as ready with fallback
            if (mounted) {
              setViewerReady(true);
              setLoading(false);
              console.log('🎯 Mars Cesium viewer with enhanced fallback globe ready');
            }
          }
        }

      } catch (error) {
        console.error('❌ Error creating Mars Cesium viewer:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    // Call the async function
    initializeMarsViewer();

    // Clean up on unmount
    return () => {
      mounted = false;
      console.log('🧹 Component unmounting, cleaning up...');

      if (viewerInstanceRef.current && !viewerInstanceRef.current.isDestroyed()) {
        console.log('🧹 Cleaning up Mars Cesium viewer');
        viewerInstanceRef.current.destroy();
        viewerInstanceRef.current = null;
      }
    };
  }, []);

  // Additional cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Function to get pin color based on location type
  const getPinColor = (type) => {
    if (type.includes('Orbiter') || type.includes('NASA Orbiter') || type.includes('ESA Orbiter') || type.includes('ISRO Orbiter') || type.includes('UAE Orbiter') || type.includes('CNSA Orbiter')) {
      return Cesium.Color.GREEN; // Satellites
    } else if (type.includes('Lander') || type.includes('Rover') || type.includes('NASA Lander') || type.includes('NASA Rover')) {
      return Cesium.Color.BLUE; // Human technology
    } else {
      return Cesium.Color.YELLOW; // Natural landmarks
    }
  };

  // Function to get pin height based on location type
  const getPinHeight = (type) => {
    if (type.includes('Orbiter') || type.includes('NASA Orbiter') || type.includes('ESA Orbiter') || type.includes('ISRO Orbiter') || type.includes('UAE Orbiter') || type.includes('CNSA Orbiter')) {
      return 600000; // Higher for satellites (600km orbital altitude)
    } else {
      return 300000; // Standard height for surface locations (300km)
    }
  };

  // Function to add location pins to the Mars globe
  const addLocationPins = (viewer, filter = locationFilter) => {
    console.log('📍 Adding location pins to Mars globe...');
    console.log('📍 Current locationFilter state:', filter);

    // Clear existing pins first
    const entitiesToRemove = [];
    viewer.entities.values.forEach(entity => {
      if (entity.id && (entity.id.startsWith('mars_pin_') || entity.id.startsWith('mars_beacon_line_') || entity.id.startsWith('mars_clickable_'))) {
        entitiesToRemove.push(entity);
      }
    });
    entitiesToRemove.forEach(entity => viewer.entities.remove(entity));
    console.log('🗑️ Cleared', entitiesToRemove.length, 'existing entities');

    // Filter locations based on selected filter
    let filteredLocations = marsLocations;
    if (filter === 'natural') {
      filteredLocations = marsLocations.filter(loc =>
        !loc.type.includes('Lander') &&
        !loc.type.includes('Rover') &&
        !loc.type.includes('Orbiter')
      );
      console.log('🌋 Natural filter applied, locations:', filteredLocations.map(l => `${l.name} (${l.type})`));
    } else if (filter === 'human-tech') {
      filteredLocations = marsLocations.filter(loc =>
        loc.type.includes('Lander') || loc.type.includes('Rover') || loc.type.includes('Orbiter')
      );
      console.log('🤖 Human tech filter applied, locations:', filteredLocations.map(l => `${l.name} (${l.type})`));
    } else {
      console.log('📍 All locations filter applied, total:', marsLocations.length);
    }

    console.log(`📍 Final filter result: ${filter} - showing ${filteredLocations.length} of ${marsLocations.length} locations`);

    filteredLocations.forEach((location) => {
      try {
        const pinColor = getPinColor(location.type);
        const pinHeight = getPinHeight(location.type);

        // Create surface point position (only for surface locations)
        const surfacePosition = Cesium.Cartesian3.fromDegrees(
          location.coordinates.longitude,
          location.coordinates.latitude,
          1000 // Just above surface for the base point
        );

        // Create beacon top position
        const beaconTopPosition = Cesium.Cartesian3.fromDegrees(
          location.coordinates.longitude,
          location.coordinates.latitude,
          pinHeight // Variable height based on type
        );

        // Create beacon line (vertical line from surface to text)
        const beaconLine = viewer.entities.add({
          id: `mars_beacon_line_${location.id}`,
          name: `${location.name} Beacon`,
          polyline: {
            positions: [surfacePosition, beaconTopPosition],
            width: 8,
            material: new Cesium.PolylineGlowMaterialProperty({
              glowPower: 0.2,
              color: pinColor
            }),
            clampToGround: false
          }
        });

        // Create surface point (base of beacon) - only for surface locations
        if (!location.type.includes('Orbiter')) {
          const surfacePoint = viewer.entities.add({
            id: `mars_pin_base_${location.id}`,
            name: `${location.name} Base`,
            position: surfacePosition,
            point: {
              pixelSize: 16,
              color: pinColor,
              outlineColor: Cesium.Color.BLACK,
              outlineWidth: 3,
              heightReference: Cesium.HeightReference.CLAMP_TO_GROUND
            }
          });
        }

        // Create clickable area at appropriate level (larger for easier clicking)
        const clickablePosition = location.type.includes('Orbiter') ? beaconTopPosition : surfacePosition;
        const clickableArea = viewer.entities.add({
          id: `mars_clickable_${location.id}`,
          name: `${location.name} Clickable Area`,
          position: clickablePosition,
          point: {
            pixelSize: 80, // Increased from 32 for easier clicking
            color: Cesium.Color.TRANSPARENT,
            outlineColor: Cesium.Color.TRANSPARENT,
            heightReference: location.type.includes('Orbiter') ? Cesium.HeightReference.NONE : Cesium.HeightReference.CLAMP_TO_GROUND
          },
          locationData: location
        });

        // Create text label at beacon top (now also clickable)
        const textLabel = viewer.entities.add({
          id: `mars_pin_label_${location.id}`,
          name: `${location.name} Label`,
          position: beaconTopPosition,
          point: {
            pixelSize: 1, // Very small point just for picking
            color: Cesium.Color.TRANSPARENT,
            outlineColor: Cesium.Color.TRANSPARENT
          },
          label: {
            text: location.name,
            font: '16pt sans-serif',
            fillColor: Cesium.Color.WHITE,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 4,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.CENTER,
            horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
            pixelOffset: new Cesium.Cartesian2(0, 0),
            heightReference: Cesium.HeightReference.NONE,
            showBackground: true,
            backgroundColor: new Cesium.Color(0, 0, 0, 0.8),
            backgroundPadding: new Cesium.Cartesian2(10, 8),
            scaleByDistance: new Cesium.NearFarScalar(1000000, 1.0, 20000000, 0.8),
            translucencyByDistance: new Cesium.NearFarScalar(1000000, 1.0, 20000000, 1.0)
          },
          locationData: location // Make text labels clickable too
        });

        console.log(`✅ Added ${location.type} pin for ${location.name} (${pinColor === Cesium.Color.YELLOW ? 'Natural' : pinColor === Cesium.Color.BLUE ? 'Human Tech' : 'Satellite'})`);

      } catch (error) {
        console.error(`❌ Failed to add pin for ${location.name}:`, error);
      }
    });

    // Remove existing click handler if it exists
    if (clickHandlerRef.current) {
      clickHandlerRef.current.destroy();
      clickHandlerRef.current = null;
      console.log('🗑️ Removed existing click handler');
    }

    // Add click event handler for pins
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    clickHandlerRef.current = handler; // Store reference

    handler.setInputAction((click) => {
      const pickedObject = viewer.scene.pick(click.position);

      if (Cesium.defined(pickedObject)) {
        // First check if the picked object has locationData directly (for labels and other entities)
        if (pickedObject.id && pickedObject.id.locationData) {
          const location = pickedObject.id.locationData;
          console.log(`📍 Clicked on ${location.name} label`);
          setSelectedLocation(location);
          setSidebarVisible(true);
          return;
        }

        // Fallback to ID parsing for other entity types
        if (pickedObject.id && pickedObject.id.id) {
          const entityId = pickedObject.id.id;
          let locationId = null;

          // Check if it's a Mars location entity (any part of the beacon)
          if (entityId.startsWith('mars_pin_')) {
            locationId = entityId.replace('mars_pin_', '').replace('_base', '').replace('_top', '').replace('_label', '');
          } else if (entityId.startsWith('mars_beacon_line_')) {
            locationId = entityId.replace('mars_beacon_line_', '');
          } else if (entityId.startsWith('mars_clickable_')) {
            locationId = entityId.replace('mars_clickable_', '');
          }

          if (locationId) {
            const location = marsLocations.find(loc => loc.id === locationId);

            if (location) {
              console.log(`📍 Clicked on ${location.name} beacon`);
              setSelectedLocation(location);
              setSidebarVisible(true);
            }
          }
        }
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    console.log('✅ Location pins and click handlers added');
  };

  return (
    <div className="w-full h-[calc(100vh-80px)] relative bg-black">
      <div
        ref={viewerRef}
        className="w-full h-full"
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
      />

      {/* Mars Header */}
      <div className="absolute top-4 left-4 bg-black bg-opacity-75 text-white p-4 rounded-lg min-w-64">
        <h2 className="text-2xl font-bold mb-2 text-red-400">
          🌌 Mars Globe Explorer
        </h2>
        <p className="text-sm text-gray-300 mb-4">
          Interactive 3D view of the Red Planet
        </p>

        {/* Mars Facts */}
        <div className="space-y-2 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-400">Diameter:</span>
            <span className="text-white">6,779 km</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Gravity:</span>
            <span className="text-white">38% of Earth</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Temperature:</span>
            <span className="text-white">-65°C average</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">Day Length:</span>
            <span className="text-white">24h 37m</span>
          </div>
        </div>

        {/* Location Filter */}
        <div className="mt-4 space-y-2">
          <label className="text-xs text-gray-300 block">Filter by Type:</label>
          <select
            value={locationFilter}
            onChange={(e) => {
              const newFilter = e.target.value;
              if (newFilter !== locationFilter) {
                setLocationFilter(newFilter);
                // Clear selection when filtering
                setSelectedLocation(null);
                setSidebarVisible(false);
                // Re-render pins with new filter
                const currentViewer = viewerInstanceRef.current;
                if (currentViewer && viewerReady) {
                  addLocationPins(currentViewer, newFilter);
                }
              }
            }}
            className="w-full bg-gray-700 text-white px-3 py-2 rounded text-sm border border-gray-600 focus:border-blue-500 focus:outline-none"
          >
            <option value="all">All ({marsLocations.length})</option>
            <option value="natural">🌋 Natural Features ({marsLocations.filter(loc => !loc.type.includes('Lander') && !loc.type.includes('Rover') && !loc.type.includes('Orbiter')).length})</option>
            <option value="human-tech">🤖 Human Technology ({marsLocations.filter(loc => loc.type.includes('Lander') || loc.type.includes('Rover') || loc.type.includes('Orbiter')).length})</option>
          </select>
        </div>

        {/* Controls */}
        <div className="mt-4 flex flex-col space-y-2">
          <button
            onClick={() => {
              const currentViewer = viewerInstanceRef.current;
              if (currentViewer) {
                currentViewer.camera.setView({
                  destination: Cesium.Cartesian3.fromDegrees(0, 0, 8000000.0),
                });
              }
            }}
            className="w-full bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
          >
            🏠 Reset View
          </button>

          <button
            onClick={() => window.history.back()}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
          >
            ← Back to Mars Hub
          </button>
        </div>
      </div>

      {/* Loading Notice */}
      {loading && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-black bg-opacity-90 text-white p-6 rounded-lg text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold mb-2">Loading Mars...</h3>
          <p className="text-sm text-gray-300 mb-2">
            Initializing 3D Mars visualization
          </p>
          <p className="text-xs text-gray-400">
            Powered by Cesium Ion Mars assets
          </p>
        </div>
      )}

      {/* Instructions */}
      {viewerReady && !loading && (
        <div className="absolute bottom-4 right-4 bg-black bg-opacity-75 text-white p-4 rounded-lg max-w-sm">
          <h4 className="text-sm font-semibold mb-2 text-red-400">🖱️ How to Explore</h4>
          <ul className="text-xs text-gray-300 space-y-1">
            <li>• <strong>Left Click + Drag:</strong> Rotate Mars</li>
            <li>• <strong>Right Click + Drag:</strong> Zoom in/out</li>
            <li>• <strong>Middle Click + Drag:</strong> Pan view</li>
            <li>• <strong>Scroll Wheel:</strong> Zoom</li>
            <li>• <strong>Click Pins or Labels:</strong> Learn about locations</li>
          </ul>
        </div>
      )}

      {/* Location Information Sidebar */}
      {sidebarVisible && selectedLocation && (
        <div className="absolute top-4 right-4 bg-black bg-opacity-90 text-white p-6 rounded-lg max-w-md shadow-2xl border border-red-500">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-xl font-bold text-red-400 mb-1">{selectedLocation.name}</h3>
              <span className="text-sm bg-red-600 px-2 py-1 rounded text-white">
                {selectedLocation.type}
              </span>
            </div>
            <button
              onClick={() => setSidebarVisible(false)}
              className="text-gray-400 hover:text-white text-xl"
            >
              ×
            </button>
          </div>

          <p className="text-gray-300 text-sm mb-4 leading-relaxed">
            {selectedLocation.description}
          </p>

          {selectedLocation.image && (
            <div className="mb-4">
              <img
                src={selectedLocation.image}
                alt={`${selectedLocation.name} - Mars surface`}
                className="w-full h-48 object-cover rounded-lg border border-gray-600"
                onError={(e) => {
                  e.target.style.display = 'none';
                  console.log(`Failed to load image for ${selectedLocation.name}`);
                }}
                onLoad={(e) => {
                  e.target.style.display = 'block';
                }}
                style={{ display: 'block' }}
              />
            </div>
          )}

          <div className="mb-4">
            <h4 className="text-sm font-semibold text-red-400 mb-2">📍 Key Facts</h4>
            <ul className="text-xs text-gray-300 space-y-1">
              {selectedLocation.facts.map((fact, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-red-400 mr-2">•</span>
                  <span>{fact}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-4">
            <h4 className="text-sm font-semibold text-red-400 mb-2">🎯 Scientific Significance</h4>
            <p className="text-xs text-gray-300 italic">
              {selectedLocation.significance}
            </p>
          </div>

          <div className="text-xs text-gray-400 border-t border-gray-600 pt-3">
            <div className="flex justify-between">
              <span>Coordinates:</span>
              <span>{selectedLocation.coordinates.latitude.toFixed(1)}°N, {selectedLocation.coordinates.longitude.toFixed(1)}°E</span>
            </div>
          </div>

          <div className="mt-4 flex space-x-2">
            <button
              onClick={() => {
                const currentViewer = viewerInstanceRef.current;
                if (currentViewer) {
                  // Fly to the location
                  currentViewer.camera.flyTo({
                    destination: Cesium.Cartesian3.fromDegrees(
                      selectedLocation.coordinates.longitude,
                      selectedLocation.coordinates.latitude,
                      2000000 // Closer view
                    ),
                    duration: 2.0
                  });
                }
              }}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
            >
              🛸 Fly To Location
            </button>
            <button
              onClick={() => setSidebarVisible(false)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarsGlobeVisualizer;
